import React, { FC, useCallback, useEffect, useRef, useState } from "react"
import { ApexOptions } from "apexcharts"
import { Box } from "@mui/system"
import { Grid, IconButton, Typography, Button } from "@mui/material"
import ArrowBackIcon from '@mui/icons-material/ArrowBackRounded'
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardRounded'
import CloseIcon from '@mui/icons-material/CloseRounded'
import format from 'date-fns/format'
import endOfWeek from 'date-fns/endOfWeek'
import startOfWeek from 'date-fns/startOfWeek'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { TabGroupFilter, SensorChart } from "src/components"
import { DataLogger } from "src/api"
import { SearchParamsKeys } from "src/enums"
import { useDataLoggerReadingsRequest } from "src/hooks/api"
import { useOutsideAlerter } from "src/hooks/ui"

type ApexChartDataObj = {
  x: any;
  y: any;
  fillColor?: string;
  strokeColor?: string;
  meta?: any;
  goals?: any;
};

type ApexChartData =
  | (number | null)[]
  | ApexChartDataObj[]
  | [number, number | null][]
  | [number, (number | null)[]][];

const chartOfReadingsTabs = ({
  humidityData,
  temperatureData,
  dewpointData,
  isLoading,
}: {
  humidityData: ApexOptions["series"];
  temperatureData: ApexOptions["series"];
  dewpointData: ApexOptions["series"];
  isLoading: boolean;
}) => {
  return [
    {
      status: "Temperature",
      content: <SensorChart
        data={temperatureData}
        isLoading={isLoading}
      />,
    },
    {
      status: "Humidity",
      content: <SensorChart
        data={humidityData}
        options={{
          yaxis: {
            min: 0,
            max: 100,
          },
        }}
        isLoading={isLoading}
      />,
    },
    {
      status: "Dewpoint",
      content: <SensorChart
        data={dewpointData}
        isLoading={isLoading}
      />,
    }
  ]
}

interface Props {
  dataLogger: DataLogger;
}

const STEP = 7

export const DataLoggerChartTabs: FC<Props> = ({ dataLogger }) => {
  const [timeFrom, setTimeFrom] = useState<Date>(startOfWeek(new Date(), { weekStartsOn: 1 }))
  const [timeTo, setTimeTo] = useState<Date>(endOfWeek(new Date(), { weekStartsOn: 1 }))
  const [datetime, setDatetime] = useState<Date | null>(startOfWeek(new Date(), { weekStartsOn: 1 }))
  const [isOpenDatePicker, setIsOpenDatePicker] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const wrapperRef = useRef(null)
  useOutsideAlerter({ ref: wrapperRef, callback: () => setIsOpenDatePicker(false)})
  const { data: dataLoggerReadings, refetch: refetchReadings, isRefetching, isInitialLoading } = useDataLoggerReadingsRequest({
    deviceId: dataLogger.deviceId,
    params: { dateFrom: timeFrom.toISOString(), dateTo: timeTo.toISOString() },
    options: { enabled: false }
  })

  useEffect(() => {
    if (timeFrom && timeTo) refetchReadings()
  }, [timeFrom, timeTo, refetchReadings])

  const getSensorBarChartData = useCallback(
    ({
      readout,
      length,
    }: {
      readout: {
        time: string;
        value: number;
      }[];
      length: number;
    }): ApexChartData => {
      const data: ApexChartDataObj[] = []
      for (let i = 0; i <= length; i++) {
        const shiftedTime = new Date(new Date(timeTo).setUTCHours(new Date(timeTo).getUTCHours() - i, 59, 59))
        const reading = readout.find((_reading) => {
          const readingTime = new Date((_reading as any).time).getTime()
          const diff = shiftedTime.getTime() - readingTime
          const diffInMinutes = Number((diff / 1000 / 60).toFixed(0))
          return diffInMinutes >= 0 && diffInMinutes < 60
        })
        data.unshift({
          x: shiftedTime.getTime(),
          y: reading?.value || 0,
          fillColor: "#26a2fc",
        })
      }
      return data
    },
    [timeTo]
  )

  const getHumidityChartData = (): ApexOptions["series"] => {
    const oneWeek = STEP * 24 // one week in hours
    const data = getSensorBarChartData({
      readout: (dataLoggerReadings?.humidity || []).map((reading) => ({
        time: reading.time,
        value: reading.humidity,
      })),
      length: oneWeek,
    })
    return [
      {
        name: "Humidity",
        data,
      },
    ]
  }

  const getTemperatureChartData = (): ApexOptions["series"] => {
    const oneWeek = STEP * 24 // one week in hours
    const data = getSensorBarChartData({
      readout: (dataLoggerReadings?.temperature || []).map((reading) => ({
        time: reading.time,
        value: reading.temperatureF,
      })),
      length: oneWeek,
    })
    return [
      {
        name: "Temperature",
        data,
      },
    ]
  }

  const getDewpointChartData = (): ApexOptions["series"] => {
    const oneWeek = STEP * 24 // one week in hours
    const data = getSensorBarChartData({
      readout: (dataLoggerReadings?.dewpoint || []).map((reading) => ({
        time: reading.time,
        value: reading.deviationF,
      })),
      length: oneWeek,
    })
    return [
      {
        name: "Dewpoint",
        data,
      },
    ]
  }

  const isLastWeek = useCallback(() => {
    const endOfWeekDate = endOfWeek(new Date(), { weekStartsOn: 1 })
    return timeTo.getDate() === endOfWeekDate.getDate()
      && timeTo.getMonth() === endOfWeekDate.getMonth()
      && timeTo.getFullYear() === endOfWeekDate.getFullYear()
  }, [timeTo])()

  const handleGoBack = () => {
    if (isRefetching) return

    const newTimeFrom = new Date(new Date(timeFrom).setDate(new Date(timeFrom).getDate() - STEP))
    const newTimeTo = new Date(new Date(timeTo).setDate(new Date(timeTo).getDate() - STEP))
    setTimeFrom(newTimeFrom)
    setTimeTo(newTimeTo)
    setDatetime(newTimeFrom)
  }

  const handleGoForward = () => {
    if (isLastWeek || isRefetching) return

    const newTimeFrom = new Date(new Date(timeFrom).setDate(new Date(timeFrom).getDate() + STEP))
    const newTimeTo = new Date(new Date(timeTo).setDate(new Date(timeTo).getDate() + STEP))
    setTimeFrom(newTimeFrom)
    setTimeTo(newTimeTo)
    setDatetime(newTimeFrom)
  }

  const resetRange = () => {
    const newTimeFrom = startOfWeek(new Date(), { weekStartsOn: 1 })
    const newTimeTo = endOfWeek(new Date(), { weekStartsOn: 1 })
    setTimeFrom(newTimeFrom)
    setTimeTo(newTimeTo)
    setDatetime(newTimeFrom)
  }

  const onAcceptDatetime = (newDatetime: Date | null) => {
    const startOfWeekDate = startOfWeek(newDatetime || timeFrom, { weekStartsOn: 1 })
    const endOfWeekDate = endOfWeek(newDatetime || timeTo, { weekStartsOn: 1 })
    setDatetime(startOfWeekDate)
    setTimeFrom(startOfWeekDate)
    setTimeTo(endOfWeekDate)
    setIsOpenDatePicker(false)
  }

  const toggleDatePicker = (event: React.MouseEvent<HTMLButtonElement>) => {
    setIsOpenDatePicker(!isOpenDatePicker)
    setAnchorEl(!isOpenDatePicker ? event.currentTarget : null)
  }

  return (
    <Box>
      <Grid>
        <Grid container alignItems="center">
          <Grid item>
            <IconButton aria-label="go-back" color="primary" onClick={handleGoBack} disabled={isRefetching}>
              <ArrowBackIcon />
            </IconButton>
          </Grid>
          <Grid item px={1}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                open={isOpenDatePicker}
                value={datetime}
                onChange={() => {}}
                onAccept={onAcceptDatetime}
                renderInput={(props: any) => (
                  <Button variant="text" onClick={toggleDatePicker}>
                    <Typography variant="body1">
                      {`${format(timeFrom, 'PP')} - ${format(timeTo, 'PP')}`}
                    </Typography>
                  </Button>
                )}
                PopperProps={{
                  placement: "bottom-end",
                  anchorEl: anchorEl,
                  ref: wrapperRef,
                }}
                disableFuture
                closeOnSelect
                disableHighlightToday
              />
            </LocalizationProvider>
          </Grid>
          <Grid item>
            <IconButton aria-label="go-forward" color="primary" onClick={handleGoForward} disabled={isLastWeek || isRefetching}>
              <ArrowForwardIcon />
            </IconButton>
          </Grid>
          <Grid item>
            <IconButton aria-label="reset" color="primary" onClick={resetRange} disabled={isLastWeek || isRefetching}>
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Grid>
      <Grid>
        <TabGroupFilter
          name={SearchParamsKeys.Chart}
          statusList={chartOfReadingsTabs({
            humidityData: getHumidityChartData(),
            temperatureData: getTemperatureChartData(),
            dewpointData: getDewpointChartData(),
            isLoading: isRefetching || isInitialLoading,
          })}
        />
      </Grid>
    </Box>
  )
}
